import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL, LOCATION_ID } from "..";
import { Modal } from "react-bootstrap";
import Breadcrumb from "../components/Breadcrumb";

const AccountsPage = () => {
  const [formData, setFormData] = useState({
    type: "",
    bankName: "",
    bankAccountTitle: "",
    bankAccountNo: "",
    bankIbanNumber: "",
    accountTitle: "",
    accountNo: "",
  });
  const [error, setError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [tableError, setTableError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editAccountId, setEditAccountId] = useState(null);

  // Account types for dropdown (common payment methods in Pakistan)
  const accountTypes = [
    { value: "bank", label: "Bank Account" },
    { value: "jazzcash", label: "JazzCash" },
    { value: "easypaisa", label: "Easypaisa" },
    { value: "nayapay", label: "NayaPay" },
    { value: "sadapay", label: "SadaPay" },
    { value: "payoneer", label: "Payoneer" },
    { value: "paypal", label: "PayPal" },
    { value: "upaisa", label: "UPaisa" },
    { value: "vouchers", label: "Vouchers" },
  ];

  // Check if account type is bank
  const isBankAccount = formData.type === "bank";

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
        
      const response = await fetch(`${API_URL}/api/accounts/location/${LOCATION_ID}`, {
        method: "GET",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (response.status === 200) {
        if (!Array.isArray(data.accounts)) {
          throw new Error("Invalid data format: accounts is not an array");
        }
        setAccounts(data.accounts || []);
        setTableError("");
      } else {
        throw new Error(data.message || "Failed to fetch accounts");
      }
    } catch (err) {
      console.error("Fetch accounts error:", err);
      setTableError(err.message || "Unable to load accounts. Please try again.");
      setAccounts([]);
      toast.error(err.message || "Unable to fetch accounts", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  // Handle account type change
  const handleAccountTypeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: value,
      bankName: value === "bank" ? prev.bankName : "",
      bankAccountTitle: value === "bank" ? prev.bankAccountTitle : "",
      bankAccountNo: value === "bank" ? prev.bankAccountNo : "",
      bankIbanNumber: value === "bank" ? prev.bankIbanNumber : "",
      accountTitle: value !== "bank" ? prev.accountTitle : "",
      accountNo: value !== "bank" ? prev.accountNo : "",
    }));
    setError("");
  };

  // Validate form
  const validateForm = () => {
    if (!formData.type) return "Account type is required";

    if (isBankAccount) {
      if (!formData.bankName.trim()) return "Bank name is required";
      if (!formData.bankAccountTitle.trim()) return "Bank account title is required";
      if (!formData.bankAccountNo.trim()) return "Bank account number is required";
      if (!formData.bankIbanNumber.trim()) return "IBAN number is required";
      if (!formData.bankIbanNumber.startsWith("PK")) {
        return "IBAN must start with 'PK' for Pakistani banks";
      }
      if (formData.bankIbanNumber.length < 24) {
        return "IBAN number must be at least 24 characters long";
      }
    } else {
      if (!formData.accountTitle.trim()) return "Account title is required";
      if (!formData.accountNo.trim()) return "Account number is required";
      if (!/^\d{11,13}$/.test(formData.accountNo)) {
        return "Account number must be 11-13 digits";
      }
    }

    return "";
  };

  // Handle form submission (add or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      toast.error(validationError, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setLoading(true);
    try {
      let payload = { type: formData.type };
      if (isBankAccount) {
        payload = {
          ...payload,
          bankName: formData.bankName,
          bankAccountTitle: formData.bankAccountTitle,
          bankAccountNo: formData.bankAccountNo,
          bankIbanNumber: formData.bankIbanNumber,
          locationId:LOCATION_ID,
        };
      } else {
        payload = {
          ...payload,
          accountTitle: formData.accountTitle,
          accountNo: formData.accountNo,
          locationId:LOCATION_ID,
        };
      }

      let response;
      if (isEditing) {
        // Update account
        response = await fetch(`${API_URL}/api/accounts/${editAccountId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
        });
      } else {
        // Add new account
        response = await fetch(`${API_URL}/api/accounts`, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
          },
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (response.status === 201 || response.status === 200) {
        const successMsg = isEditing ? "Account updated successfully" : "Account added successfully";
        toast.success(successMsg, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        if (isEditing) {
          setAccounts((prev) =>
            prev.map((acc) => (acc.id === editAccountId ? data.account : acc))
          );
        } else {
          setAccounts((prev) => [...prev, data.account]);
        }
        resetForm();
        setShowModal(false);
      } else {
        throw new Error(data.message || `Failed to ${isEditing ? "update" : "add"} account`);
      }
    } catch (err) {
      console.error(`${isEditing ? "Update" : "Submit"} error:`, err);
      const errorMsg = err.message || `Unable to ${isEditing ? "update" : "save"} account`;
      setError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit (open modal with pre-filled data)
  const handleEdit = (id) => {
    const account = accounts.find((acc) => acc.id === id);
    if (account) {
      setFormData({
        type: account.type || "",
        bankName: account.bankName || "",
        bankAccountTitle: account.bankAccountTitle || "",
        bankAccountNo: account.bankAccountNo || "",
        bankIbanNumber: account.bankIbanNumber || "",
        accountTitle: account.accountTitle || "",
        accountNo: account.accountNo || "",
      });
      setIsEditing(true);
      setEditAccountId(id);
      setShowModal(true);
      setError("");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/accounts/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (response.status === 200) {
        toast.success("Account deleted successfully", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setAccounts((prev) => prev.filter((account) => account.id !== id));
      } else {
        throw new Error(data.message || "Failed to delete account");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.message || "Unable to delete account", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      type: "",
      bankName: "",
      bankAccountTitle: "",
      bankAccountNo: "",
      bankIbanNumber: "",
      accountTitle: "",
      accountNo: "",
    });
    setError("");
    setIsEditing(false);
    setEditAccountId(null);
  };

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  return (
    <>
      <MasterLayout>
        <Breadcrumb title="Accounts" />

        <div className="card basic-data-table">
          <div className="card-header">
            <div className="row">
              <h5 className="card-title mb-0 col-md-6">List of Accounts</h5>
              <div className="col-md-6 text-end">
                <button
                  className="btn btn-primary"
                  style={{ background: "#144BD6", borderColor: "#144BD6", fontSize: 12 }}
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  Add New Account
                </button>
              </div>
            </div>
          </div>
          <div className="card-body">
            {tableError && (
              <div className="alert alert-danger mb-3">{tableError}</div>
            )}
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Account Details</th>
                  <th scope="col">Created Date</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account?.id}>
                    <td>
                      {accountTypes.find((type) => type.value === account.type)?.label ||
                        account.type}
                    </td>
                    <td>
                      {account.type === "bank"
                        ? `${account.bankAccountTitle} - ${account.bankAccountNo}`
                        : `${account.accountTitle} - ${account.accountNo}`}
                    </td>
                    <td>
                      {account.createdAt
                        ? new Date(account.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary me-2"
                        onClick={() => handleEdit(account.id)}
                        disabled={loading}
                      >
                        <i className="ph ph-pencil">Edit</i>
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(account.id)}
                        disabled={loading}
                      >
                        <i className="ph ph-trash">Delete</i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {accounts.length === 0 && !tableError && (
              <div className="text-center mt-3">
                <p>No accounts found. Add a new account to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Account Modal */}
        <Modal show={showModal} size="lg" onHide={() => {
          resetForm();
          setShowModal(false);
        }}>
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? "Edit Account" : "Add New Account"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="basic-data-table mt-4">
              {error && <div className="alert alert-danger mb-3">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-bold">Account Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleAccountTypeChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select Account Type</option>
                    {accountTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bank-specific fields */}
                {isBankAccount && (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Bank Name</label>
                      <input
                        type="text"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter bank name (e.g., HBL, UBL)"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Bank Account Title</label>
                      <input
                        type="text"
                        name="bankAccountTitle"
                        value={formData.bankAccountTitle}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter account holder's full name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Bank Account Number</label>
                      <input
                        type="text"
                        name="bankAccountNo"
                        value={formData.bankAccountNo}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter 12-16 digit account number"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">IBAN Number</label>
                      <input
                        type="text"
                        name="bankIbanNumber"
                        value={formData.bankIbanNumber}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter IBAN (starts with PK, 24 characters)"
                        required
                      />
                    </div>
                  </>
                )}

                {/* Non-bank fields */}
                {!isBankAccount && (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Account Title</label>
                      <input
                        type="text"
                        name="accountTitle"
                        value={formData.accountTitle}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter account holder's name"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Account Number</label>
                      <input
                        type="text"
                        name="accountNo"
                        value={formData.accountNo}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter mobile number or account number (11-13 digits)"
                        required
                      />
                    </div>
                  </>
                )}

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ background: "#db6f2f", borderColor: "#db6f2f" }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isEditing ? "Updating..." : "Saving..."}
                      </>
                    ) : (
                      isEditing ? "Update Account" : "Save Account"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      resetForm();
                      setShowModal(false);
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </Modal.Body>
        </Modal>

        <ToastContainer />
      </MasterLayout>
    </>
  );
};

export default AccountsPage;